import React, { useState, useEffect } from 'react';
import TriggerCard from './TriggerCard';
import SummaryDashboard from '../common/SummaryDashboard';
import LoadingState from '../common/LoadingState';
import { Info } from 'lucide-react';
import { buildApiUrl } from '../../../../services/apiConfig';

const TriggerNotificationsTab = ({ clientBehavioralData, selectedClient }) => {
  const [expandedTrigger, setExpandedTrigger] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  const toggleTrigger = (id) => {
    if (expandedTrigger === id) {
      setExpandedTrigger(null);
    } else {
      setExpandedTrigger(id);
    }
  };

  // Helper function to extract JSON from markdown code blocks
  const extractJsonFromMarkdown = (text) => {
    // Check if the text contains markdown code blocks
    if (text.includes('```json')) {
      // Extract the content between the code blocks
      const match = text.match(/```json\s*([\s\S]*?)\s*```/);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    // If no markdown formatting, return the original text
    return text;
  };

  const safelyExtractAndParseJson = (text) => {
    try {
      // First, try to extract JSON from markdown code blocks
      if (text && text.includes('```json')) {
        const match = text.match(/```json\s*([\s\S]*?)\s*```/);
        if (match && match[1]) {
          return JSON.parse(match[1].trim());
        }
      }

      // If that fails, try to find any JSON-like structure in the text
      const jsonRegex = /\[\s*{[\s\S]*}\s*\]/g;
      const possibleJson = text.match(jsonRegex);
      if (possibleJson && possibleJson[0]) {
        return JSON.parse(possibleJson[0]);
      }

      // If no JSON structure found, try parsing the entire text
      return JSON.parse(text);
    } catch (err) {
      console.warn('Failed to parse JSON response:', err);

      // As a fallback, create a synthetic trigger to show the error
      return [{
        id: "error_trigger",
        title: "Error Processing Trigger Data",
        type: "System Error",
        description: "There was an error processing the trigger data. The system received a malformed response.",
        priority: "Medium",
        status: "Error",
        date: new Date().toISOString().split('T')[0],
        suggestedActions: [
          "Refresh the page and try again",
          "Contact system administrator if the problem persists"
        ]
      }];
    }
  };

  // const fetchTriggers = async () => {
  //   if (!selectedClient) return;

  //   // Generate a unique session key for this page load
  //   // This will be different each time the page is reloaded
  //   const pageLoadKey = sessionStorage.getItem('page_load_key');

  //   // Check if we have cached triggers for this client and this page load
  //   const cachedTriggersKey = `triggers_${selectedClient.id}_${pageLoadKey}`;
  //   const cachedTriggers = sessionStorage.getItem(cachedTriggersKey);

  //   if (cachedTriggers) {
  //     setTriggers(JSON.parse(cachedTriggers));
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     // Create a FormData object
  //     const formData = new FormData();

  //     // Create a JSON string for the client data
  //     const clientData = JSON.stringify({
  //       "client_record": {
  //         "before": {
  //           "id": selectedClient.id,
  //           "name": selectedClient.name,
  //           "age": selectedClient.age || 58,
  //           "occupation": selectedClient.occupation || "Executive, Tech Industry",
  //           "annual_income": selectedClient.annual_income || 850000,
  //           "net_worth": selectedClient.net_worth || 12500000,
  //           "risk_profile": selectedClient.risk_profile || "Moderate-Aggressive",
  //           "investment_horizon": selectedClient.investment_horizon || "10-15 years",
  //           "portfolio": selectedClient.portfolio || {
  //             "total_value": 8750000,
  //             "allocation": {
  //               "equities": 65,
  //               "fixed_income": 20,
  //               "alternatives": 10,
  //               "cash": 5
  //             }
  //           },
  //           "goals": selectedClient.goals || [
  //             "Retirement at 65",
  //             "College funding for children",
  //             "Estate planning"
  //           ]
  //         },
  //         "after": {
  //           "id": selectedClient.id,
  //           "name": selectedClient.name,
  //           "age": selectedClient.age || 58,
  //           "occupation": "Retired",
  //           "annual_income": 250000,
  //           "net_worth": 12800000,
  //           "risk_profile": "Moderate",
  //           "investment_horizon": "5-10 years",
  //           "portfolio": {
  //             "total_value": 9100000,
  //             "allocation": {
  //               "equities": 65,
  //               "fixed_income": 20,
  //               "alternatives": 10,
  //               "cash": 5
  //             }
  //           },
  //           "goals": [
  //             "Retirement income planning",
  //             "Estate planning",
  //             "Philanthropic initiatives"
  //           ]
  //         }
  //       },
  //       "metadata": {
  //         "update_timestamp": new Date().toISOString()
  //       }
  //     });

  //     // Create a Blob from the JSON string
  //     const fileBlob = new Blob([clientData], { type: 'application/json' });

  //     // Append the file to the FormData
  //     formData.append('file', fileBlob, 'client-data.json');

  //     // Append the prompt to the FormData
  //     formData.append('prompt', promptText);

  //     // Make the API call
  //     const response = await fetch(buildApiUrl('JAMBA', '/custom-analyze'), {
  //       method: 'POST',
  //       body: formData
  //     });

  //     if (!response.ok) {
  //       throw new Error(`API responded with status: ${response.status}`);
  //     }

  //     const result = await response.json();

  //     // Extract JSON from markdown if needed
  //     const cleanJsonString = extractJsonFromMarkdown(result.data);

  //     // Parse the JSON string
  //     const parsedTriggers = JSON.parse(cleanJsonString);

  //     // Save to state and sessionStorage
  //     setTriggers(parsedTriggers);
  //     sessionStorage.setItem(cachedTriggersKey, JSON.stringify(parsedTriggers));

  //   } catch (err) {
  //     console.error('Error fetching triggers:', err);
  //     setError(err.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchTriggers = async () => {
    if (!selectedClient) return;

    // Generate a unique session key for this page load
    const pageLoadKey = sessionStorage.getItem('page_load_key');

    // Check if we have cached triggers for this client and this page load
    const cachedTriggersKey = `triggers_${selectedClient.id}_${pageLoadKey}`;
    const cachedTriggers = sessionStorage.getItem(cachedTriggersKey);

    if (cachedTriggers) {
      try {
        setTriggers(JSON.parse(cachedTriggers));
        return;
      } catch (err) {
        console.warn('Failed to parse cached triggers, fetching fresh data');
        // Continue with the fetch if parsing cached data fails
      }
    }

    setLoading(true);
    try {
      // Create a FormData object
      const formData = new FormData();

      // Create a JSON string for the client data
      const clientData = JSON.stringify({
        "client_record": {
          "before": {
            "id": selectedClient.id,
            "name": selectedClient.name,
            "age": selectedClient.age || 58,
            "occupation": selectedClient.occupation || "Executive, Tech Industry",
            "annual_income": selectedClient.annual_income || 850000,
            "net_worth": selectedClient.net_worth || 12500000,
            "risk_profile": selectedClient.risk_profile || "Moderate-Aggressive",
            "investment_horizon": selectedClient.investment_horizon || "10-15 years",
            "portfolio": selectedClient.portfolio || {
              "total_value": 8750000,
              "allocation": {
                "equities": 65,
                "fixed_income": 20,
                "alternatives": 10,
                "cash": 5
              }
            },
            "goals": selectedClient.goals || [
              "Retirement at 65",
              "College funding for children",
              "Estate planning"
            ]
          },
          "after": {
            "id": selectedClient.id,
            "name": selectedClient.name,
            "age": selectedClient.age || 58,
            "occupation": "Retired",
            "annual_income": 250000,
            "net_worth": 12800000,
            "risk_profile": "Moderate",
            "investment_horizon": "5-10 years",
            "portfolio": {
              "total_value": 9100000,
              "allocation": {
                "equities": 65,
                "fixed_income": 20,
                "alternatives": 10,
                "cash": 5
              }
            },
            "goals": [
              "Retirement income planning",
              "Estate planning",
              "Philanthropic initiatives"
            ]
          }
        },
        "metadata": {
          "update_timestamp": new Date().toISOString()
        }
      });

      // Create a Blob from the JSON string
      const fileBlob = new Blob([clientData], { type: 'application/json' });

      // Append the file to the FormData
      formData.append('file', fileBlob, 'client-data.json');

      // Append the prompt to the FormData
      formData.append('prompt', promptText);

      // Make the API call
      const response = await fetch(buildApiUrl('JAMBA', '/custom-analyze'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();

      // Use our enhanced function to safely extract and parse JSON
      const parsedTriggers = safelyExtractAndParseJson(result.data);

      // Save to state and sessionStorage
      setTriggers(parsedTriggers);
      sessionStorage.setItem(cachedTriggersKey, JSON.stringify(parsedTriggers));

    } catch (err) {
      console.error('Error fetching triggers:', err);
      setError(err.message);

      // Set fallback triggers for better user experience
      setTriggers([{
        id: "error_" + Date.now(),
        title: "Error Loading Triggers",
        type: "System Error",
        description: `Failed to load trigger notifications: ${err.message}. Please try refreshing the page.`,
        priority: "Medium",
        status: "Error",
        date: new Date().toISOString().split('T')[0],
        suggestedActions: [
          "Refresh the page",
          "Check your network connection",
          "Contact support if the issue persists"
        ]
      }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Generate a unique page load key when the component mounts
    // This will happen once per page load
    if (!sessionStorage.getItem('page_load_key')) {
      sessionStorage.setItem('page_load_key', Date.now().toString());
    }

    if (selectedClient) {
      fetchTriggers();
    }
  }, [selectedClient]);

  const promptText = `Analyze this client data file which contains both the previous ("before") and current ("after") states of a client record.
  Compare these two states and identify meaningful changes that would require financial advisor attention or action.
  Format the response as a JSON array with the following structure for each trigger:
  [
    {
      "id": "trig_" + [random 8-character alphanumeric string],
      "title": "Brief descriptive title of the trigger",
      "type": "Portfolio Change | Market Event | Life Event | Compliance | Opportunity",
      "description": "Detailed description of what changed and why it matters to the advisor",
      "priority": "High | Medium | Low",
      "status": "New",
      "date": [use the update_timestamp from the metadata, formatted as YYYY-MM-DD],
      "suggestedActions": [
        "Specific action the advisor should consider taking",
        "Another recommended action",
        "A third potential action if applicable"
      ]
    }
  ]
  Priority guidelines:
  - High: Immediate attention required, significant financial impact, regulatory concerns
  - Medium: Should be addressed within a week, moderate financial impact
  - Low: For awareness, can be addressed during regular review cycles
  Focus on these key changes:
  1. Life events (retirement, marriage, children, etc.)
  2. Significant income or net worth changes (>10%)
  3. Risk profile or investment horizon adjustments
  4. Portfolio allocation or value changes (>5%)
  5. Goal modifications
  6. Compliance or tax status changes
  DO NOT return any additional text.
  For each detected change, provide specific, actionable suggestions that the advisor should consider.
  Provide 1 triggers based on the significance of the changes detected.
  If no significant changes are detected, return an empty array.`;

  if (loading) {
    return <LoadingState message="Loading trigger notifications data..." />;
  }

  if (error && (!triggers || triggers.length === 0)) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <p className="text-red-500">Error loading triggers: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          Monitors wealth triggers and market events to identify timely outreach opportunities.
        </p>
        <button
          onClick={() => setShowPrompt(!showPrompt)}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          title="Show prompt information"
        >
          <Info size={18} className="text-gray-500" />
        </button>
      </div>

      {showPrompt && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Prompt:</h4>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap">{promptText}</pre>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-2">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {triggers.filter(t => t.priority === 'High').length} High Priority
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {triggers.filter(t => t.priority === 'Medium').length} Medium Priority
          </span>
        </div>
      </div>

      {triggers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No active triggers found for this client.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {triggers.map((trigger) => (
            <TriggerCard
              key={trigger.id}
              trigger={trigger}
              isExpanded={expandedTrigger === trigger.id}
              onToggle={toggleTrigger}
            />
          ))}
        </div>
      )}

    </div>
  );
};

export default TriggerNotificationsTab;